import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { getOs, isInAppWebview } from './ua-detect';
import {
  INSTALL_BUTTON_ICON_SVG,
  ensureInstallGuideStyles,
  isInstalledAsRelatedApp,
  isStandaloneDisplay,
  promptInstallOrGuide,
  INSTALL_PROMPT_KEY,
} from './install-guide.js';

/**
 * "Add Ember to your Home Screen" — the React trigger for install-guide.js.
 *
 * Chromium fires `beforeinstallprompt` once, early, and each bundle's entry
 * parks it on window[INSTALL_PROMPT_KEY]. A tap fires that native prompt when
 * it is there and falls back to the platform guide when it is not — which is
 * ALWAYS on iOS (no install API exists), and on Chromium whenever the prompt
 * was already used, refused, or withheld. The guide itself (markup, skin and
 * open/close behavior) lives in install-guide.js, so this component and the
 * server-rendered story page teach the identical taps rather than two dialects.
 *
 * Nothing renders until an effect has looked at the browser: the server has no
 * answer, and rendering one to correct it would be a hydration mismatch on
 * every page that mounts this. Nothing renders once Ember is already installed
 * either, where the button would be an instruction to do what has been done.
 */

type InstallWindow = Window & { [INSTALL_PROMPT_KEY]?: Event };

export function AddToHomeButton({
  className = '',
  label = 'Add Ember to your Home Screen',
  ariaLabel,
  showIcon = true,
  onActivate,
  source = 'nav',
  dataLog,
}: {
  className?: string;
  label?: string;
  /** Which surface this button sits on — rides every install.* event so the
   *  nav's funnel and a story page's funnel stay tellable apart. */
  source?: string;
  /** Drop the glyph — for rows of plain text links, where an icon on one
   *  item alone would read as a different kind of thing. */
  showIcon?: boolean;
  /** Fired on tap, before the prompt or guide opens: lets a menu that hosts
   *  this close itself instead of sitting open behind the modal. */
  onActivate?: () => void;
  /** The full sentence, for surfaces whose visible label is a short word and
   *  for widths where CSS folds that word away (the nav bar does both). */
  ariaLabel?: string;
  /** ui.clicked name — must be registered in analytics/metadata/registry.js. */
  dataLog?: string;
}): ReactElement | null {
  const [offer, setOffer] = useState(false);

  useEffect(() => {
    const os = getOs();
    // A phone can always be taught the manual path; anything else is offered
    // the button only once Chromium has actually handed us a prompt.
    // Dev only: always offered, so the chip and its guide can be looked at on
    // a desktop browser without device emulation (a query flag would be
    // tidier, but port forwarding does not always carry one through).
    // import.meta.env.DEV is false in every build, so this disappears from the
    // shipped bundle — to see the real gate, run a build or emulate a phone.
    const forced = import.meta.env.DEV;
    // Android Chromium can also answer "already installed?" from a plain
    // browser tab (getInstalledRelatedApps, resolved async below); iOS never
    // can, so there the button stays — an installed iPhone browsing in Safari
    // is indistinguishable from one that never installed.
    let installedElsewhere = false;
    const evaluate = () => setOffer(
      forced || (
        !installedElsewhere
        && !isStandaloneDisplay(window)
        && (os === 'ios' || os === 'android' || !!(window as InstallWindow)[INSTALL_PROMPT_KEY])
      ),
    );
    evaluate();
    void isInstalledAsRelatedApp(window).then((already) => {
      if (already) {
        installedElsewhere = true;
        evaluate();
      }
    });
    ensureInstallGuideStyles(document);
    // install.completed (a standalone launch, Chromium's appinstalled) is
    // reported by the bundle at start, on every page — initInstallCompletion
    // in each bundle's main — not here, where it was only observable on the
    // page that mounts this button (2026-09-06).

    // The prompt can arrive after mount (a slow load, a bfcache restore), and
    // installing elsewhere in the browser retires the affordance mid-visit.
    // Chromium only hands out a prompt when the app is NOT installed, so an
    // arriving prompt outranks a stale related-apps answer.
    const onPrompt = () => {
      installedElsewhere = false;
      evaluate();
    };
    const onInstalled = () => {
      (window as InstallWindow)[INSTALL_PROMPT_KEY] = undefined;
      setOffer(false);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [source]);

  const handleClick = useCallback(() => {
    onActivate?.();
    void promptInstallOrGuide({
      // An in-app browser (Facebook, Instagram, TikTok…) has no install of any
      // kind, so it is taught how to leave for the real one instead.
      os: isInAppWebview() ? 'webview' : (getOs() === 'android' ? 'android' : 'ios'),
      host: window.location.host,
      source,
      win: window,
    });
  }, [onActivate, source]);

  if (!offer) return null;

  return (
    <button
      type="button"
      className={className}
      onClick={handleClick}
      aria-label={ariaLabel || label}
      title={ariaLabel || label}
      {...(dataLog ? { 'data-log': dataLog } : {})}
    >
      {showIcon ? (
        <span className="a2hs-button-ico" aria-hidden="true" dangerouslySetInnerHTML={{ __html: INSTALL_BUTTON_ICON_SVG }} />
      ) : null}
      <span className="a2hs-button-label">{label}</span>
    </button>
  );
}
